import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { joinWith } from "../../common/utils/array.util";
import HandleClicksOutside from "../HandleClicksOutside";
import "./MultiselectionDropdown.css";
import { Flex } from "./Flex";
import Icon from "./Icon";

export type MultiSelectionDropdownOption = {
    value: string;
    label: string;
};

type Props = {
    optionList?: MultiSelectionDropdownOption[];
    entries?: [string, string][];
    label?: string;
    defaultLabel?: string;
    initialValues?: string[];
    direction?: "row" | "column";
    className?: string;
    onChange?: (values: string[]) => void;
};

const MultiSelectionDropdown = function MultiSelectionDropdown(props: Props) {
    const {
        optionList,
        entries,
        label,
        defaultLabel,
        initialValues,
        direction,
        className,
        onChange,
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [currentValues, setCurrentValues] = useState<string[] | undefined>(
        initialValues,
    );

    useEffect(() => {
        setCurrentValues(initialValues);
    }, [initialValues]);

    const options =
        optionList ||
        entries?.map<MultiSelectionDropdownOption>(([key, value]) => ({
            value: key,
            label: value,
        }));
    if (!options) return <div className="dropdown" />;

    const currentSelectedLabels = (currentValues || []).map(
        (value) => options.filter((option) => option.value === value)[0].label,
    );
    const selectedText = joinWith(currentSelectedLabels, ", ", "and");
    const dropdownLabel =
        currentValues && currentValues?.length > 0 ? selectedText : defaultLabel;

    return (
        <div
            className={classNames(
                className,
                "multiselection-dropdown__container",
                `multiselection-dropdown__container--${direction}`,
            )}
        >
            <HandleClicksOutside
                className="multiselection-dropdown__group"
                onClickOutside={() => {
                    setIsOpen(false);
                }}
                disabled={!isOpen}
            >
                <div
                    className={classNames("multiselection-dropdown", {
                        "multiselection-dropdown--open": isOpen,
                    })}
                >
                    <Flex.Row
                        onClick={() => {
                            setIsOpen(!isOpen);
                        }}
                    >
                        <Flex.Column className="multiselection-dropdown__current-column">
                            {label && (
                                <label className="multiselection-dropdown__label">
                                    {label}
                                </label>
                            )}
                            <div className="multiselection-dropdown__current">
                                {dropdownLabel}
                            </div>
                        </Flex.Column>
                        <span className="multiselection-dropdown__triangle">
                            {isOpen ? (
                                <Icon.ExpandLess.Small />
                            ) : (
                                <Icon.ExpandMore.Small />
                            )}
                        </span>
                    </Flex.Row>
                    {isOpen && (
                        <div className="multiselection-dropdown__options">
                            {options.map((option) => (
                                <div
                                    className="multiselection-dropdown__option"
                                    key={`multiselection-dropdown-${option.value}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!onChange) return;
                                            if (currentValues?.includes(option.value)) {
                                                const newValues = currentValues.filter(
                                                    (value) => value !== option.value,
                                                );
                                                onChange(newValues);
                                                setCurrentValues(newValues);
                                            } else {
                                                const newValues = [
                                                    ...(currentValues || []),
                                                    option.value,
                                                ];
                                                onChange(newValues);
                                                setCurrentValues(newValues);
                                            }
                                        }}
                                    >
                                        <input
                                            className="multiselection-dropdown__option__checkbox"
                                            type="checkbox"
                                            checked={currentValues?.includes(
                                                option.value,
                                            )}
                                            onChange={() => {}}
                                        />
                                        <span>{option.label || option.value}</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </HandleClicksOutside>
        </div>
    );
};

export default MultiSelectionDropdown;
